import { Box, MenuItem, Pagination, Select, Stack, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridPaginationModel, GridRowsProp, GridValidRowModel, gridPageCountSelector, gridPageSelector, useGridApiContext, useGridSelector } from "@mui/x-data-grid";
import GridColBase from "../util/GridColBase";
import { ColorHexCodes, getHexCode } from "../util/Color";
import PaginationItem from "@mui/material/PaginationItem";
import { useState } from "react";
import StyledButton from "./StyledButton";
import { Action } from "../util/func";

const PAGE_SIZE_OPTIONS = [25, 50, 100];

export default function CustomGrid<T extends GridValidRowModel>({ cols, rows, actionLabel, onActionClicked }: { cols: GridColDef<T>[]; rows: GridRowsProp<T>; actionLabel: string; onActionClicked?: Action<T> }) {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: PAGE_SIZE_OPTIONS[0],
    page: 0,
  });

  if (rows.length === 0) {
    return <Typography textAlign="center">データがありません</Typography>;
  }

  cols = cols.map((c) => ({ ...c, ...GridColBase }));
  if (onActionClicked !== undefined) {
    cols = [
      ...cols,
      {
        field: "delete",
        headerName: "操作",
        width: 160,
        sortable: false,
        filterable: false,
        hideable: false,
        resizable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <StyledButton sx={{ paddingLeft: 2, paddingRight: 2, color: `#${ColorHexCodes.DeepRed}`, fontWeight: "bold", fontSize: 16 }} buttonStyle="text" onClick={() => onActionClicked(params.row)}>
            {actionLabel}
          </StyledButton>
        )
      },
    ];
  }

  return (
    <DataGrid
      sx={{
        border: `2px solid #${ColorHexCodes.Main}`,
        borderRadius: 2,
        backgroundColor: `#${ColorHexCodes.Main}`,
        "--DataGrid-rowBorderColor": "none",
        "& .app-data-odd": {
          backgroundColor: `#${ColorHexCodes.SubList}`,
        },
        "& .app-data-even": {
          backgroundColor: `#${ColorHexCodes.White}`,
        },
        "& .MuiDataGrid-virtualScroller": {
          backgroundColor: `#${ColorHexCodes.GridGray}`,
        },
        "& .MuiDataGrid-columnHeaders": {
          fontSize: 18,
          backgroundColor: `#${ColorHexCodes.Main}`,
          "--DataGrid-containerBackground": `#${ColorHexCodes.Main}`,
          color: "white",
          "& .MuiDataGrid-sortIcon": {
            color: "white",
          },
          "& .MuiDataGrid-columnSeparator": {
            visibility: "hidden",
          },
        },
        "& .MuiDataGrid-columnHeader": {
          borderRight: `2px solid #${ColorHexCodes.White}`,
          backgroundColor: getHexCode("SubBlue")
        },
        "& .MuiDataGrid-columnHeader--last": {
          borderRight: "none",
        },
        "& .MuiDataGrid-row.MuiDataGrid-row--firstVisible": {
          "--rowBorderColor": "none",
        },
        "& .MuiDataGrid-cell": {
          fontSize: 18,
          border: "none",
          borderRight: `2px solid #${ColorHexCodes.Main}`,
        },
        "& .MuiDataGrid-cell:last-child": {
          borderRight: "none",
        },
        "& .MuiDataGrid-footerContainer": {
          borderTop: `2px solid #${ColorHexCodes.Main}`,
          backgroundColor: `#${ColorHexCodes.White}`
        }
      }}
      columns={cols}
      rows={rows}
      initialState={{
        pagination: { paginationModel: paginationModel },
      }}
      pageSizeOptions={PAGE_SIZE_OPTIONS}
      getRowClassName={(params) => `app-data-${params.indexRelativeToCurrentPage % 2 ? "odd" : "even"}`}
      paginationMode="client"
      onPaginationModelChange={(newPaginationModel) => setPaginationModel(newPaginationModel)}
      paginationModel={paginationModel}
      slots={{
        pagination: () =>
          CustomPagination({
            onPageSizeChanged: (pageSize) => setPaginationModel({ ...paginationModel, pageSize: pageSize }),
            pageSize: paginationModel.pageSize,
          }),
      }}
      columnHeaderHeight={62}
      rowHeight={62}
      scrollbarSize={0}
    />
  );
}

type CustomPaginationProps = {
  pageSize: number;
  onPageSizeChanged: (pageSize: number) => void;
};

function CustomPagination(props: CustomPaginationProps) {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <Stack direction="row" justifyItems="center" pr="14px">
      <Typography alignContent="center">行数/1ページ：</Typography>
      <Box sx={{ pl: 1, pr: 3 }}>
        <Select sx={{ height: 36 }} value={props.pageSize} size="small" onChange={(event) => props.onPageSizeChanged(Number(event.target.value))} variant="outlined">
          {PAGE_SIZE_OPTIONS.map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box sx={{ pt: "2px" }}>
        <Pagination
          color="primary"
          variant="outlined"
          shape="rounded"
          page={page + 1}
          count={pageCount}
          renderItem={(props2) => (
            <PaginationItem
              {...props2}
              sx={{
                "&.Mui-selected": {
                  backgroundColor: `#${ColorHexCodes.Main}`,
                  color: "white",
                  fontWeight: "bold",
                  "&:hover": {
                    backgroundColor: `#${ColorHexCodes.Main}`,
                  },
                },
                "&:hover": {
                  backgroundColor: `#${ColorHexCodes.PagenationGray}`,
                },
                backgroundColor: `#${ColorHexCodes.PagenationGray}`,
              }}
            />
          )}
          onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
      </Box>
    </Stack>
  );
}